import * as React from "react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { SiteHeader } from "@/components/layout/TopBar";
import {
  AppSidebar,
  SidebarProvider,
  SidebarInset,
} from "@/components/layout/Sidebar";
import { useAuth } from "@/hooks/useAuth";
import { useSidebarBreakpoint } from "@/hooks/use-mobile";
import { AppModeProvider } from "@/contexts/AppModeContext";
import { ActiveOrganizationProvider } from "@/contexts/ActiveOrganizationContext";
import { ActiveLocationProvider } from "@/contexts/ActiveLocationContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import {
  PageHeaderProvider,
  PageHeaderSlot,
  usePageHeaderActive,
} from "@/components/layout/PageHeaderSlot";

const PUBLIC_ROUTES = ["/login", "/forgot-password", "/reset-password", "/sign-up", "/confirm-email", "/onboarding"];

export function AppLayout() {
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  if (isPublicRoute) {
    return <Outlet />;
  }

  return <ProtectedAppLayout />;
}

// On tablet widths the page provides the app's main header; everywhere else the
// shared TopBar is shown. The slot target is always mounted so a page can portal
// its header into it; it's only made visible when a page header is active.
function AppHeader() {
  const active = usePageHeaderActive();
  return (
    <>
      {!active && <SiteHeader />}
      <PageHeaderSlot className={active ? undefined : "hidden"} />
    </>
  );
}

function ProtectedAppLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const breakpoint = useSidebarBreakpoint();

  // Desktop always shows the full sidebar — collapsing is disabled there. Only
  // tablet toggles between the icon rail (default) and the expanded sidebar;
  // crossing a breakpoint snaps back to that viewport's default. Done during
  // render via the React "reset state on prop change" pattern.
  const isDesktop = breakpoint === "desktop";
  const viewportDefaultOpen = breakpoint !== "tablet";
  const [open, setOpen] = React.useState(viewportDefaultOpen);
  const [lastBreakpoint, setLastBreakpoint] = React.useState(breakpoint);
  if (lastBreakpoint !== breakpoint) {
    setLastBreakpoint(breakpoint);
    setOpen(viewportDefaultOpen);
  }
  // Force-expanded on desktop, regardless of any toggle or Ctrl+B shortcut.
  const sidebarOpen = isDesktop ? true : open;

  if (!isAuthenticated || isLoading) {
    return <Outlet />;
  }

  return (
    <NotificationsProvider>
    <AppModeProvider>
    <ActiveOrganizationProvider>
    <ActiveLocationProvider>
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={isDesktop ? undefined : setOpen}
      style={
        {
          "--header-height": "3.5rem",
        } as React.CSSProperties
      }
      className="flex min-h-svh bg-sidebar"
    >
      <AppSidebar variant="sidebar" />
      {/* Card floats 12px on all sides. From tablet up (md+), the sidebar is
          present and frames the left, so the left padding shrinks to ~0 to avoid
          a left gap larger than the right. On mobile the sidebar is an overlay
          sheet, so the 12px left padding stays to keep the card off the edge. */}
      <SidebarInset className="flex flex-1 bg-sidebar p-3 md:pl-px">
        <PageHeaderProvider>
          {/* `data-main-panel` is where in-page panels such as cook mode are
              portalled, so they cover the main area and leave the sidebar. */}
          <div
            data-main-panel
            className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-(--shell-border) bg-(--shell-panel-bg) shadow-(--shell-shadow)"
          >
            <AppHeader />
            <main className="flex flex-1 flex-col overflow-y-auto">
              <Outlet />
            </main>
          </div>
        </PageHeaderProvider>
      </SidebarInset>
    </SidebarProvider>
    </ActiveLocationProvider>
    </ActiveOrganizationProvider>
    </AppModeProvider>
    </NotificationsProvider>
  );
}
